import { useState, useEffect, useRef, useCallback } from 'react';
import { AddressValue, Language } from '@/shared/types';
import { MapPin, Loader2, ChevronDown } from 'lucide-react';

// ============================================
// Types
// ============================================

interface Suggestion {
  label: string;
  street: string;
  city: string;
  postalCode: string;
  department?: string;
}

type ActiveField = 'street' | 'city' | 'postalCode' | null;

// ============================================
// In-memory cache
// ============================================

const cache = new Map<string, { data: Suggestion[]; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000;

function getCached(key: string): Suggestion[] | null {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  return null;
}

function setCache(key: string, data: Suggestion[]) {
  cache.set(key, { data, ts: Date.now() });
  if (cache.size > 200) {
    const oldest = [...cache.entries()].sort((a, b) => a[1].ts - b[1].ts)[0];
    if (oldest) cache.delete(oldest[0]);
  }
}

// ============================================
// French Address API — contextual queries
// ============================================

async function searchFrench(query: string, type?: string): Promise<Suggestion[]> {
  if (!query || query.length < 2) return [];
  const cacheKey = `fr:${type || 'all'}:${query}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const params = new URLSearchParams({ q: query, limit: '6' });
    if (type) params.set('type', type);
    const res = await fetch(`https://api-adresse.data.gouv.fr/search/?${params}`);
    if (!res.ok) return [];
    const data = await res.json();
    const results: Suggestion[] = (data.features || []).map((f: any) => ({
      label: f.properties.label || '',
      street: f.properties.name || '',
      city: f.properties.city || '',
      postalCode: f.properties.postcode || '',
      department: f.properties.context?.split(',')[0]?.trim() || '',
    }));
    setCache(cacheKey, results);
    return results;
  } catch {
    return [];
  }
}

// ============================================
// Nominatim API for international
// ============================================

async function searchNominatim(query: string, country: string): Promise<Suggestion[]> {
  if (!query || query.length < 2) return [];
  const cacheKey = `osm:${country}:${query}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const cc = country.length === 2 ? `&countrycodes=${country.toLowerCase()}` : '';
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}${cc}&limit=6`,
      { headers: { 'Accept-Language': 'fr' } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    const results: Suggestion[] = data.map((item: any) => {
      const addr = item.address || {};
      return {
        label: item.display_name || '',
        street: [addr.road, addr.house_number].filter(Boolean).join(' ') || '',
        city: addr.city || addr.town || addr.village || addr.municipality || '',
        postalCode: addr.postcode || '',
        department: addr.state || addr.county || '',
      };
    });
    setCache(cacheKey, results);
    return results;
  } catch {
    return [];
  }
}

// ============================================
// Debounced search hook — per field
// ============================================

function useDebouncedSuggestions(
  country: string,
  query: string,
  fieldType: ActiveField,
  enabled: boolean,
  delay = 400,
) {
  const [results, setResults] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!enabled || !query || query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      const isFrance = country.toLowerCase() === 'france' || country.toLowerCase() === 'fr';

      let data: Suggestion[];
      if (isFrance) {
        // Use the French API type parameter for contextual results
        const apiType = fieldType === 'postalCode' ? 'municipality' :
                        fieldType === 'city' ? 'municipality' : undefined;
        data = await searchFrench(query, apiType);
      } else {
        data = await searchNominatim(query, country);
      }

      setResults(data);
      setLoading(false);
    }, delay);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [query, country, fieldType, enabled, delay]);

  return { results, loading };
}

// ============================================
// Countries list
// ============================================

const COUNTRIES = [
  'France', 'Belgique', 'Suisse', 'Canada', 'Luxembourg',
  'Espagne', 'Italie', 'Allemagne', 'Portugal', 'Royaume-Uni',
  'États-Unis', 'Maroc', 'Tunisie', 'Algérie', 'Sénégal',
];

// ============================================
// Suggestion Dropdown sub-component
// ============================================

interface SuggestionDropdownProps {
  results: Suggestion[];
  loading: boolean;
  visible: boolean;
  query: string;
  language: Language;
  onSelect: (s: Suggestion) => void;
  renderItem?: (s: Suggestion) => React.ReactNode;
}

function SuggestionDropdown({ results, loading, visible, query, language, onSelect, renderItem }: SuggestionDropdownProps) {
  if (!visible) return null;

  if (loading && query.length >= 2) {
    return (
      <div className="absolute z-50 left-0 right-0 mt-1 bg-card border-2 border-border rounded-xl shadow-lg p-3 flex items-center justify-center">
        <Loader2 size={16} className="text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (results.length > 0) {
    return (
      <div className="absolute z-50 left-0 right-0 mt-1 bg-card border-2 border-border rounded-xl shadow-lg max-h-52 overflow-y-auto">
        {results.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(s)}
            className="w-full text-left px-4 py-3 text-sm hover:bg-primary/10 focus:bg-primary/10 transition-colors border-b border-border last:border-b-0"
          >
            {renderItem ? renderItem(s) : (
              <>
                <span className="font-medium">{s.label}</span>
              </>
            )}
          </button>
        ))}
      </div>
    );
  }

  if (query.length >= 2 && !loading) {
    return (
      <div className="absolute z-50 left-0 right-0 mt-1 bg-card border-2 border-border rounded-xl shadow-lg p-4 text-sm text-muted-foreground text-center">
        {language === 'fr' ? 'Aucun résultat' : language === 'es' ? 'Sin resultados' : 'No results'}
      </div>
    );
  }

  return null;
}

// ============================================
// Main AddressField Component
// ============================================

interface AddressFieldProps {
  value: AddressValue;
  onChange: (value: AddressValue) => void;
  language: Language;
  required?: boolean;
  showDepartment?: boolean;
  defaultCountry?: string;
  label?: string;
}

export function AddressField({
  value, onChange, language, required, showDepartment = false,
  defaultCountry = 'France', label,
}: AddressFieldProps) {
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [streetQ, setStreetQ] = useState(value.street || '');
  const [cityQ, setCityQ] = useState(value.city || '');
  const [postalQ, setPostalQ] = useState(value.postalCode || '');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const country = value.country || defaultCountry;

  // Set default country on mount
  useEffect(() => {
    if (!value.country) {
      onChange({ ...value, country: defaultCountry });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local inputs when value changes externally
  useEffect(() => { setStreetQ(value.street || ''); }, [value.street]);
  useEffect(() => { setCityQ(value.city || ''); }, [value.city]);
  useEffect(() => { setPostalQ(value.postalCode || ''); }, [value.postalCode]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Three independent debounced searches
  const streetSearch = useDebouncedSuggestions(country, streetQ, 'street', activeField === 'street');
  const citySearch = useDebouncedSuggestions(country, cityQ, 'city', activeField === 'city');
  const postalSearch = useDebouncedSuggestions(country, postalQ, 'postalCode', activeField === 'postalCode');

  const applySelection = useCallback((s: Suggestion) => {
    onChange({
      ...value,
      street: s.street || value.street,
      city: s.city || value.city,
      postalCode: s.postalCode || value.postalCode,
      department: s.department || value.department,
    });
    setStreetQ(s.street || value.street || '');
    setCityQ(s.city || value.city || '');
    setPostalQ(s.postalCode || value.postalCode || '');
    setActiveField(null);
  }, [value, onChange]);

  const l = {
    fr: { country: 'Pays', street: 'Adresse / Rue', city: 'Ville', postalCode: 'Code postal', department: 'Département / Région' },
    en: { country: 'Country', street: 'Street address', city: 'City', postalCode: 'Postal code', department: 'State / Region' },
    es: { country: 'País', street: 'Dirección', city: 'Ciudad', postalCode: 'Código postal', department: 'Departamento / Región' },
  }[language];

  const inputClass = "w-full p-3 bg-card border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors";

  return (
    <div className="space-y-3" ref={wrapperRef}>
      {label && (
        <div className="text-base font-medium flex items-center gap-2">
          <MapPin size={18} className="text-primary" />
          {label}
          {required && <span className="text-destructive text-sm">*</span>}
        </div>
      )}

      {/* Country selector */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-muted-foreground">{l.country}</label>
        <div className="relative">
          <select
            value={country}
            onChange={e => {
              onChange({ ...value, country: e.target.value });
              setStreetQ(''); setCityQ(''); setPostalQ('');
              setActiveField(null);
            }}
            className={inputClass + " appearance-none pr-10"}
          >
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            <option value="other">{language === 'fr' ? 'Autre...' : language === 'es' ? 'Otro...' : 'Other...'}</option>
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Street with suggestions */}
      <div className="space-y-1 relative">
        <label className="text-sm font-medium text-muted-foreground">{l.street}</label>
        <div className="relative">
          <input
            type="text"
            value={streetQ}
            onChange={e => {
              setStreetQ(e.target.value);
              onChange({ ...value, street: e.target.value });
              setActiveField('street');
            }}
            onFocus={() => { if (streetQ.length >= 2) setActiveField('street'); }}
            placeholder={language === 'fr' ? 'Ex: 12 rue de la Paix' : language === 'es' ? 'Ej: Calle Mayor 12' : 'E.g. 12 Main Street'}
            className={inputClass}
          />
          {streetSearch.loading && (
            <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
          )}
        </div>
        <SuggestionDropdown
          results={streetSearch.results}
          loading={streetSearch.loading}
          visible={activeField === 'street'}
          query={streetQ}
          language={language}
          onSelect={applySelection}
          renderItem={s => (
            <>
              <span className="font-medium">{s.street}</span>
              <br />
              <span className="text-muted-foreground text-xs">{s.postalCode} {s.city}{s.department ? ` — ${s.department}` : ''}</span>
            </>
          )}
        />
      </div>

      {/* Postal code + City row — both with suggestions */}
      <div className="grid grid-cols-2 gap-3">
        {/* Postal code */}
        <div className="space-y-1 relative">
          <label className="text-sm font-medium text-muted-foreground">{l.postalCode}</label>
          <input
            type="text"
            value={postalQ}
            onChange={e => {
              setPostalQ(e.target.value);
              onChange({ ...value, postalCode: e.target.value });
              setActiveField('postalCode');
            }}
            onFocus={() => { if (postalQ.length >= 2) setActiveField('postalCode'); }}
            placeholder="75001"
            className={inputClass}
          />
          <SuggestionDropdown
            results={postalSearch.results}
            loading={postalSearch.loading}
            visible={activeField === 'postalCode'}
            query={postalQ}
            language={language}
            onSelect={applySelection}
            renderItem={s => (
              <span className="text-sm"><span className="font-semibold">{s.postalCode}</span> — {s.city}</span>
            )}
          />
        </div>

        {/* City */}
        <div className="space-y-1 relative">
          <label className="text-sm font-medium text-muted-foreground">{l.city}</label>
          <input
            type="text"
            value={cityQ}
            onChange={e => {
              setCityQ(e.target.value);
              onChange({ ...value, city: e.target.value });
              setActiveField('city');
            }}
            onFocus={() => { if (cityQ.length >= 2) setActiveField('city'); }}
            placeholder={language === 'fr' ? 'Paris' : language === 'es' ? 'Madrid' : 'London'}
            className={inputClass}
          />
          <SuggestionDropdown
            results={citySearch.results}
            loading={citySearch.loading}
            visible={activeField === 'city'}
            query={cityQ}
            language={language}
            onSelect={applySelection}
            renderItem={s => (
              <span className="text-sm"><span className="font-semibold">{s.city}</span> ({s.postalCode}){s.department ? ` — ${s.department}` : ''}</span>
            )}
          />
        </div>
      </div>

      {/* Department (optional) */}
      {showDepartment && (
        <div className="space-y-1">
          <label className="text-sm font-medium text-muted-foreground">{l.department}</label>
          <input
            type="text"
            value={value.department || ''}
            onChange={e => onChange({ ...value, department: e.target.value })}
            className={inputClass}
          />
        </div>
      )}
    </div>
  );
}
